import { useContext, useState, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { string, ref } from "yup";
import {
  Grid,
  Button,
  Select,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import "../../Style/dialogstyle.scss";

import moment from "moment";
import Dialog from "@mui/material/Dialog";
import UploadImage from "../../Utils/UploadImage";
import DialogContent from "@mui/material/DialogContent";

//Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { useMutation } from "@apollo/client";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import { SelectDatePicker } from "../../Components/Include/SelectDatePicker/SelectDatePicker";
import { useDeleteImageServer } from "../CropImage/DeleteImageServer";
import { CREATE_USER, UPDATE_USER } from "../../Schema/User";

export default function TeacherForm({
  open,
  editData,
  setRefetch,
  handleClose,
  hideBackdrop,
  dialogTitle,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);

  // =================================== Upload Image ====================================
  const [profileHook, setProfileHook] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const mutationDelete = useDeleteImageServer();

  //hiden and show vibility =======================================
  const [show, setShow] = useState(false);
  const handleClick = () => setShow((pre) => !pre);

  const [loading, setLoading] = useState(false);

  const handleCloseAction = () => {
    handleClose();
    if (profileHook) {
      // delete iamge from server ====================================================
      let splitSrc = profileHook?.split(":")[4];
      mutationDelete.mutate({
        storage: "sr_music_academy",
        folder: "sr_music_images",
        file: splitSrc.split("/")[0],
      });
    }
    setProfileHook("");
    setImageFile("");
  };

  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: ({ createUser }) => {
      setLoading(false);
      // console.log("createUser::", createUser);
      if (createUser?.isSuccess === true) {
        setAlert(true, "success", createUser?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createUser?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: ({ updateUser }) => {
      setLoading(false);
      // console.log("updateUser::", updateUser);
      if (updateUser?.isSuccess === true) {
        setAlert(true, "success", updateUser?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateUser?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const validationSchema = Yup.object().shape({
    teacherId: Yup.string().required(t("required")),
    dob: Yup.string().required(t("required")),
    email: Yup.string().email("Invalid email!").required(t("required")),
    gender: Yup.string().required(t("required")),
    familyName: Yup.string().required(t("required")),
    address: Yup.string(),
    password:
      dialogTitle === "Create"
        ? Yup.string()
            .required(t("required"))
            .min(8, "Please input more then 8 charactor!")
        : Yup.string().nullable(),
    confirmPassword:
      dialogTitle === "Create"
        ? string()
            .required(t("required"))
            .oneOf([ref("password")], "Wrong password!")
        : Yup.string().nullable(),
    userName: Yup.string().required(t("required")),
    nationlity: Yup.string(),
    phoneNumber: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      teacherId: "",
      dob: "",
      email: "",
      gender: "Male",
      address: "",
      password: "",
      userName: "",
      nationlity: "",
      familyName: "",
      phoneNumber: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Update") {
        updateUser({
          variables: {
            id: editData?._id,
            input: {
              teacherId: values?.teacherId,
              dob: values?.dob ? moment(values?.dob).format("YYYY-MM-DD") : "",
              email: values?.email,
              gender: values?.gender,
              address: values?.address,
              userName: values?.userName,
              nationlity: values?.nationlity,
              familyName: values?.familyName,
              phoneNumber: values?.phoneNumber,
              profile: profileHook ? profileHook : editData?.profile,
            },
          },
        });
      } else {
        createUser({
          variables: {
            input: {
              teacherId: values?.teacherId,
              dob: values?.dob ? moment(values?.dob).format("YYYY-MM-DD") : "",
              email: values?.email,
              gender: values?.gender,
              address: values?.address,
              password: values?.password,
              userName: values?.userName,
              nationlity: values?.nationlity,
              familyName: values?.familyName,
              phoneNumber: values?.phoneNumber,
              role: "Teacher",
              profile: profileHook || "",
            },
          },
        });
      }
    },
  });
  const {
    values,
    errors,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  useEffect(() => {
    if (dialogTitle === "Create") {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (editData) {
      setFieldValue("teacherId", editData?.teacherId || "");
      setFieldValue("dob", editData?.dob || "");
      setFieldValue("email", editData?.email || "");
      setFieldValue("gender", editData?.gender || "");
      setFieldValue("address", editData?.address || "");
      setFieldValue("userName", editData?.userName || "");
      setFieldValue("nationlity", editData?.nationlity || "");
      setFieldValue("familyName", editData?.familyName || "");
      setFieldValue("phoneNumber", editData?.phoneNumber || "");
    }
  }, [editData, open]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="sm"
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_teacher")
            : t("modal_title_update_teacher")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleCloseAction}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12}>
                <UploadImage
                  editData={editData}
                  imageFile={imageFile}
                  setImageFile={setImageFile}
                  setProfileHook={setProfileHook}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_last_name")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("thead_last_name")}
                  {...getFieldProps("familyName")}
                  error={Boolean(touched?.familyName && errors?.familyName)}
                  helperText={touched?.familyName && errors?.familyName}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_first_name")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("thead_first_name")}
                  {...getFieldProps("userName")}
                  error={Boolean(touched.userName && errors.userName)}
                  helperText={touched.userName && errors.userName}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_id")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("thead_id")}
                  {...getFieldProps("teacherId")}
                  error={Boolean(touched.teacherId && errors.teacherId)}
                  helperText={touched.teacherId && errors.teacherId}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_nationality")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_nationality")}
                  {...getFieldProps("nationlity")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_date_of_birth")}
                </Typography>

                <SelectDatePicker
                  value={values?.dob}
                  onChange={(val) => setFieldValue("dob", val)}
                />
                {!!errors.dob && touched.dob && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.dob}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_gender")}
                </Typography>

                <FormControl fullWidth size="small" className="text-field">
                  <Select {...getFieldProps("gender")}>
                    <MenuItem value={"Male"}> {t("thead_male")} </MenuItem>
                    <MenuItem value={"Female"}>{t("thead_female")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_email")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("label_email")}
                  {...getFieldProps("email")}
                  error={Boolean(touched?.email && errors?.email)}
                  helperText={touched?.email && errors?.email}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_telephone")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("label_telephone")}
                  {...getFieldProps("phoneNumber")}
                />
              </Grid>

              {dialogTitle === "Create" ? (
                <>
                  <Grid item xs={6}>
                    <Typography className={t("class-name-field-title")}>
                      {t("thead_password")}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      className="text-field"
                      placeholder={t("thead_password")}
                      type={show ? "text" : "password"}
                      {...getFieldProps("password")}
                      error={Boolean(touched.password && errors.password)}
                      helperText={touched.password && errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="start"
                            onClick={handleClick}
                          >
                            {show ? (
                              <VisibilityIcon className="viewpw-icon" />
                            ) : (
                              <VisibilityOffIcon className="viewpw-icon" />
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography className={t("class-name-field-title")}>
                      {t("thead_confirm_password")}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      className="text-field"
                      placeholder={t("thead_confirm_password")}
                      type={show ? "text" : "password"}
                      {...getFieldProps("confirmPassword")}
                      error={Boolean(
                        touched.confirmPassword && errors.confirmPassword,
                      )}
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                    />
                  </Grid>
                </>
              ) : null}

              <Grid item xs={12} md={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_address")}
                </Typography>
                <TextField
                  minRows={2}
                  multiline
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("label_address")}
                  {...getFieldProps("address")}
                />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    className={t("class-btn-create")}
                    // disabled={statusProgress && progress < 100 ? true : false}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
